import api from "@/lib/api";

// On-demand, whole-page AI translation overlay. Walks visible text nodes, translates
// them via the cached /translate/batch endpoint, and applies each chunk in place as it
// arrives (progressive). A MutationObserver re-translates content React renders later.

const originals = new WeakMap();     // textNode -> original English string
const translatedVal = new WeakMap(); // textNode -> last value we wrote (to detect React resets)
const phOriginals = new WeakMap();   // element -> original placeholder
const clientCache = new Map();       // `${lang}:::${text}` -> translated
const changed = new Set();           // nodes we've mutated (for restore)
const changedPh = new Set();

let currentLang = "en";
let observer = null;
let applying = false;   // true while we're writing to the DOM ourselves
let running = false;    // true while an apply pass is in flight
let rerun = false;      // request another pass after the current one
let debounceTimer = null;
let initialized = false;

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SELECT", "OPTION", "SVG", "PATH"]);
const hasLetters = (s) => /[A-Za-z\u00C0-\u024F\u0370-\u1FFF\u3040-\uD7FF\uF900-\uFAFF]/.test(s);

function shouldSkip(node) {
  const p = node.parentElement;
  if (!p) return true;
  if (SKIP_TAGS.has(p.tagName)) return true;
  if (p.closest("[data-no-translate]")) return true;
  const v = node.nodeValue;
  if (!v || !v.trim() || !hasLetters(v)) return true;
  return false;
}

function collect(root) {
  const nodes = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (shouldSkip(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

function writeSrc(src, tr, srcToNodes, srcToPh) {
  applying = true;
  (srcToNodes.get(src) || []).forEach((n) => {
    if (n.isConnected) { n.nodeValue = tr; translatedVal.set(n, tr); changed.add(n); }
  });
  (srcToPh.get(src) || []).forEach((el) => {
    if (el.isConnected) { el.setAttribute("placeholder", tr); el.setAttribute("data-tr-ph", tr); changedPh.add(el); }
  });
  applying = false;
}

async function runPass(root, lang) {
  const nodes = collect(root);
  const srcToNodes = new Map();
  for (const node of nodes) {
    if (translatedVal.get(node) === node.nodeValue) continue; // already translated & unchanged
    const src = node.nodeValue;
    originals.set(node, src);
    if (!srcToNodes.has(src)) srcToNodes.set(src, []);
    srcToNodes.get(src).push(node);
  }

  const srcToPh = new Map();
  const phEls = root.querySelectorAll ? root.querySelectorAll("input[placeholder], textarea[placeholder]") : [];
  phEls.forEach((el) => {
    if (el.closest("[data-no-translate]")) return;
    const cur = el.getAttribute("placeholder");
    if (!cur || !hasLetters(cur)) return;
    if (phOriginals.get(el) && el.getAttribute("data-tr-ph") === cur) return; // unchanged
    const orig = phOriginals.has(el) ? phOriginals.get(el) : cur;
    phOriginals.set(el, orig);
    if (!srcToPh.has(orig)) srcToPh.set(orig, []);
    srcToPh.get(orig).push(el);
  });

  const allSrc = [...new Set([...srcToNodes.keys(), ...srcToPh.keys()])];
  if (!allSrc.length) return;

  if (observer) observer.disconnect();
  try {
    // 1) Apply cached translations instantly.
    const misses = [];
    for (const src of allSrc) {
      const key = `${lang}:::${src}`;
      if (clientCache.has(key)) writeSrc(src, clientCache.get(key), srcToNodes, srcToPh);
      else misses.push(src);
    }
    // 2) Translate misses in chunks with limited concurrency, applying each as it lands.
    const CHUNK = 40, CONCURRENCY = 3;
    const chunks = [];
    for (let i = 0; i < misses.length; i += CHUNK) chunks.push(misses.slice(i, i + CHUNK));
    let idx = 0;
    const worker = async () => {
      while (idx < chunks.length) {
        if (currentLang !== lang) return;
        const slice = chunks[idx++];
        try {
          const { data } = await api.post("/translate/batch", { texts: slice, target: lang });
          const res = data.translations || [];
          slice.forEach((src, i) => {
            const tr = res[i] != null ? res[i] : src;
            clientCache.set(`${lang}:::${src}`, tr);
            if (currentLang === lang) writeSrc(src, tr, srcToNodes, srcToPh);
          });
        } catch { /* leave untranslated */ }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, chunks.length) }, worker));
  } finally {
    if (observer && currentLang !== "en") observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }
}

async function apply(root) {
  if (currentLang === "en" || !root) return;
  if (running) { rerun = true; return; }
  running = true;
  const lang = currentLang;
  try { await runPass(root, lang); } finally {
    running = false;
    if (rerun && currentLang !== "en") { rerun = false; apply(document.body); }
  }
}

function restore() {
  if (observer) { observer.disconnect(); observer = null; }
  applying = true;
  for (const node of changed) {
    if (node.isConnected && originals.has(node)) node.nodeValue = originals.get(node);
    translatedVal.delete(node);
  }
  for (const el of changedPh) {
    if (el.isConnected && phOriginals.has(el)) el.setAttribute("placeholder", phOriginals.get(el));
    el.removeAttribute("data-tr-ph");
  }
  changed.clear();
  changedPh.clear();
  applying = false;
}

function startObserver() {
  if (observer) return;
  observer = new MutationObserver(() => {
    if (applying || currentLang === "en") return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => apply(document.body), 400);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

const translator = {
  init() { initialized = true; },
  async setLanguage(code) {
    currentLang = code || "en";
    try { document.documentElement.lang = currentLang; } catch { /* noop */ }
    if (currentLang === "en") { restore(); return; }
    startObserver();
    await apply(document.body);
  },
  getLanguage() { return currentLang; },
};

export default translator;
